import { Link } from "@tanstack/react-router";
import { Heart } from "lucide-react";

import { chrome } from "@/content/chrome";
import type { Locale } from "@/i18n/locales";
import { localePath } from "@/i18n/locales";

type FooterProps = {
  locale: Locale;
};

export function Footer({ locale }: FooterProps) {
  const copy = chrome[locale].footer;
  const year = new Date().getFullYear();

  return (
    <footer className="border-t border-border bg-card">
      <div className="mx-auto max-w-6xl px-5 py-12">
        <div className="grid grid-cols-2 gap-8 md:grid-cols-4">
          <div className="col-span-2 md:col-span-1">
            <Link
              to={localePath(locale)}
              className="mb-4 inline-flex items-center gap-2.5"
            >
              <img src="/icon-ui.webp" alt="" width={192} height={192} className="h-8 w-8 rounded-lg" />
              <span className="font-heading text-lg font-bold">Vaxy</span>
            </Link>
            <p className="text-sm leading-relaxed text-muted-foreground">
              {copy.blurb}
            </p>
            <p className="mt-4 text-xs leading-5 text-muted-foreground">
              {chrome[locale].disclaimer}
            </p>
          </div>

          <div>
            <h2 className="mb-4 font-heading text-sm font-semibold">
              {copy.product}
            </h2>
            <ul className="space-y-2.5">
              <li>
                <Link
                  to={localePath(locale, "/app")}
                  className="text-sm text-muted-foreground transition-colors hover:text-foreground"
                >
                  {copy.theApp}
                </Link>
              </li>
              <li>
                <Link
                  to={localePath(locale, "/travel")}
                  className="text-sm text-muted-foreground transition-colors hover:text-foreground"
                >
                  {copy.travelHealth}
                </Link>
              </li>
              <li>
                <Link
                  to={localePath(locale, "/developers")}
                  className="text-sm text-muted-foreground transition-colors hover:text-foreground"
                >
                  {copy.apiDocs}
                </Link>
              </li>
              <li>
                <Link
                  to={localePath(locale)}
                  hash="download"
                  className="text-sm text-muted-foreground transition-colors hover:text-foreground"
                >
                  {copy.download}
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h2 className="mb-4 font-heading text-sm font-semibold">
              {copy.community}
            </h2>
            <ul className="space-y-2.5">
              <li>
                <Link
                  to={localePath(locale, "/community")}
                  className="text-sm text-muted-foreground transition-colors hover:text-foreground"
                >
                  {copy.community}
                </Link>
              </li>
              <li>
                <Link
                  to={localePath(locale, "/about")}
                  className="text-sm text-muted-foreground transition-colors hover:text-foreground"
                >
                  {copy.about}
                </Link>
              </li>
              <li>
                <Link
                  to={localePath(locale, "/media")}
                  className="text-sm text-muted-foreground transition-colors hover:text-foreground"
                >
                  {copy.media}
                </Link>
              </li>
              <li>
                <Link
                  to={localePath(locale, "/blog")}
                  className="text-sm text-muted-foreground transition-colors hover:text-foreground"
                >
                  {copy.blog}
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h2 className="mb-4 font-heading text-sm font-semibold">
              {copy.support}
            </h2>
            <ul className="space-y-2.5">
              <li>
                <Link
                  to={localePath(locale, "/privacy")}
                  className="text-sm text-muted-foreground transition-colors hover:text-foreground"
                >
                  {copy.privacy}
                </Link>
              </li>
              <li>
                <Link
                  to={localePath(locale, "/cookies")}
                  className="text-sm text-muted-foreground transition-colors hover:text-foreground"
                >
                  {copy.cookies}
                </Link>
              </li>
              <li>
                <Link
                  to={localePath(locale, "/terms")}
                  className="text-sm text-muted-foreground transition-colors hover:text-foreground"
                >
                  {copy.terms}
                </Link>
              </li>
              <li>
                <Link
                  to={localePath(locale, "/faq")}
                  className="text-sm text-muted-foreground transition-colors hover:text-foreground"
                >
                  {copy.faq}
                </Link>
              </li>
              <li>
                <Link
                  to={localePath(locale, "/contact")}
                  className="text-sm text-muted-foreground transition-colors hover:text-foreground"
                >
                  {copy.contact}
                </Link>
              </li>
              <li>
                <Link
                  to={localePath(locale, "/delete-account")}
                  className="text-sm text-muted-foreground transition-colors hover:text-foreground"
                >
                  {copy.deleteAccount}
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-12 flex flex-col items-center justify-between gap-4 border-t border-border pt-8 md:flex-row">
          <p className="text-sm text-muted-foreground">
            © {year} Vaxy. {copy.rights}
          </p>
          <p className="flex items-center gap-1 text-sm text-muted-foreground">
            {copy.madeWith}
            <Heart className="h-3.5 w-3.5 fill-primary text-primary" />
          </p>
        </div>
      </div>
    </footer>
  );
}
